import React, { Component } from 'react';
import PropTypes from 'prop-types';

import { formatName } from '../../utils/StringUtils';
import { statusColours } from '../../constants/ColourConstants';
import { DateStyles } from "../../constants/CentricConstants";

import AutoComplete from '../Common/AutoComplete';
import Checkbox from 'material-ui/Checkbox';
import DatePicker from "material-ui/DatePicker";
import Dialog from 'material-ui/Dialog';
import FlatButton from 'material-ui/FlatButton';
import MenuItem from 'material-ui/MenuItem';

import SelectField from 'material-ui/SelectField';
import CancelledIcon from 'material-ui/svg-icons/content/clear';
import StatusIcon from 'material-ui/svg-icons/action/done';
import WaitingIcon from 'material-ui/svg-icons/action/schedule';

import CourseActions from '../../actions/CourseActionCreator';
import CourseStore from '../../stores/CourseStore';
import FilterActions from '../../actions/FilterActionCreator';
import FilterStore from '../../stores/FilterStore';
import UserActions from '../../actions/UserActionCreator';
import UserStore from '../../stores/UserStore';

const getIcon = (status) => {
    switch(status) {
        case 'cancelled':
            return <CancelledIcon color={statusColours[status]}/>;
        case 'waiting':
            return <WaitingIcon />;
        case 'enrolled':
            return <StatusIcon />;
        case 'completed':
            return <StatusIcon color={statusColours[status]}/>;
        default:
            return null;
    }
};

const sanitiseParams = (params) => {
    let changes = {};
    if (params.dateStart) {
        changes = {dateStart: params.dateStart.toLocaleString('en-GB', DateStyles.long)};
    }
    if (params.dateCompleted) {
        changes = {...changes, dateCompleted: params.dateCompleted.toLocaleString('en-GB', DateStyles.long)};
    }
    return !changes ? params : {...params, ...changes};
};

class CourseEnrolmentAddDialog extends Component {

    constructor() {
        super(...arguments);

        this.state = {
            userId:     null,
            filterId:   null,
            userList:   [],
            filterList: null,
            options: {
                status:      null,
                isMandatory: null,
                canRetake:   null
            }
        };
    }

    componentWillMount() {
        if (!this.props.byFilter) {
            this.listener = UserStore.addListener(this.getUserList);
        } else {
            this.listener = FilterStore.addListener(this.getFilterList);
            FilterActions.getAll();
        }
    }

    componentDidMount() {
        this.searchUsers = this.searchUsers.bind(this);
        this.getUserList = this.getUserList.bind(this);
        this.getFilterList = this.getFilterList.bind(this);
        this.addEnrolments = this.addEnrolments.bind(this);
        this.onStatusChange = this.onStatusChange.bind(this);
    }

    componentWillUnmount() {
        this.listener.remove();
    }

    render() {
        if (!this.props.courseId) { return null; } //guard: Course ID is required

        return (
            <Dialog
                title={!this.props.byFilter ? 'New enrolment' : 'Enrol users by filter'}
                open={this.props.open}
                modal={false}
                onRequestClose={this.props.onClose}
                actions={[
                    <FlatButton label="Cancel" key="cancel" onTouchTap={this.props.onClose}/>,
                    <FlatButton
                        primary={true}
                        label="Add"
                        onTouchTap={this.addEnrolments}
                        disabled={(!this.props.byFilter && !this.state.userId) || (!!this.props.byFilter && !this.state.filterId)}
                    />
                ]}
            >
                <div className='cent-flex-row'>
                    <div className='cent-col-2'>
                        {this.getMainField()}
                    </div>

                    <div className='cent-col-2'>
                        <SelectField
                            hintText="Status"
                            floatingLabelText="Status"
                            value={this.state.options.status}
                            fullWidth={true}
                            onChange={this.onStatusChange}
                        >
                            {this.getStatusOptions()}
                        </SelectField>
                    </div>
                    <div className='cent-col-2'>
                        <DatePicker
                            name="dateStart"
                            maxDate={this.state.options.dateCompleted || new Date()}
                            hintText="Date enrolled"
                            floatingLabelText="Date enrolled"
                            value={this.state.options.dateStart}
                            onChange={(e, value) => this.setState({options: {...this.state.options, dateStart: value}})}
                            disabled={!['enrolled', 'completed'].includes(this.state.options.status)}
                            fullWidth
                        />
                    </div>
                    <div className='cent-col-2'>
                        <DatePicker
                            name="dateCompleted"
                            minDate={this.state.options.dateStart}
                            maxDate={new Date()}
                            hintText="Date completed"
                            floatingLabelText="Date completed"
                            value={this.state.options.dateCompleted}
                            onChange={(e, value) => this.setState({options: {...this.state.options, dateCompleted: value}})}
                            disabled={this.state.options.status !== 'completed'}
                            fullWidth
                        />
                    </div>
                    <div className='cent-col-2'>
                        <div className="cent-checkbox-wrapper">
                            <div className="cent-checkbox-hint">User can retake course after completion</div>
                            <Checkbox label="Allow Retake" onCheck={(e, value) => this.setState({options: {...this.state.options, canRetake: value}})}/>
                        </div>
                    </div>
                    <div className='cent-col-2'>
                        <div className="cent-checkbox-wrapper">
                            <div className="cent-checkbox-hint">Completion requirement</div>
                            <Checkbox label="Mandatory" onCheck={(e, value) => this.setState({options: {...this.state.options, isMandatory: value}})}/>
                        </div>
                    </div>
                </div>
            </Dialog>
        );
    }

    searchUsers(searchText) {
        this.timeout && clearTimeout(this.timeout);
        if (!!searchText && searchText.length >= 3) {
            this.timeout = setTimeout(() => UserActions.search(searchText, {limit: 10}), 500);
        }
    }

    getUserList() {
        const users = UserStore.getAll();
        if (!!users) {
            const userList = users.map((user) => {
                const name = formatName(user);
                return {
                    text: name,
                    user,
                    value: <MenuItem key={user.id} value={user.id} primaryText={name}/>
                };
            });
            this.setState({userList});
        }
    }

    getFilterList() {
        const filters = FilterStore.getAll();
        if (!!filters) {
            this.setState({filterList: filters.map(({id, name}) => <MenuItem key={id} value={id} primaryText={name}/>)});
        }
    }

    getStatusOptions() {
        const statuses = CourseStore.getStatuses();
        if (!statuses || !statuses.length) { return null; }

        return statuses.map(({id, name}) => <MenuItem key={id} value={id} primaryText={name} leftIcon={getIcon(id)}/>);
    }

    onStatusChange(e, key, value) {
        this.setState({options: {
            ...this.state.options,
            status: value,
            dateStart: ['waiting', 'cancelled'].includes(value) ? null : this.state.options.dateStart || new Date(),
            dateCompleted: value === 'completed' ? new Date() : null
        }});
    }

    getMainField() {
        let field;
        if (!this.props.byFilter) {
            field = (
                <AutoComplete
                    floatingLabelText="User *"
                    maxSearchResults={5}
                    dataSource={this.state.userList}
                    onUpdateInput={this.searchUsers}
                    onNewRequest={({user}) => this.setState({userId: user.id})}
                />
            );
        } else {
            field = (
                <SelectField
                    value={this.state.filterId}
                    fullWidth={true}
                    floatingLabelText="Filter *"
                    onChange={(event, index, value) => this.setState({filterId: value})}
                >
                    {this.state.filterList}
                </SelectField>
            );
        }
        return field;
    }

    addEnrolments() {
        const params = sanitiseParams(this.state.options);
        if (!this.props.byFilter && !!this.state.userId) {
            CourseActions.enrollUser(this.props.courseId, {enrolmentUser: this.state.userId, ...params});
        } else if (!!this.state.filterId) {
            CourseActions.enrolByFilter(this.props.courseId, this.state.filterId, params);
        }
        this.props.onClose();
    }
}

CourseEnrolmentAddDialog.propTypes = {
    courseId: PropTypes.number.isRequired,
    open:     PropTypes.bool,
    byFilter: PropTypes.bool,
    onClose:  PropTypes.func
};

CourseEnrolmentAddDialog.defaultProps = {
    open:     false,
    byFilter: false,
    onClose:  () => {}
};

export default CourseEnrolmentAddDialog;
